import { useEffect, useRef, useCallback } from 'react';

/**
 * Autosave-hook for studieplan-utkast (task #300, 2026-07-02).
 *
 * Formål: beskytte mot uheldig refresh/krasj under KI-generering, KI-forbedring
 * og manuelle endringer. Alt av KI-arbeid (generering, ESCO-forslag) lever i
 * React-state til brukeren aktivt klikker "Lagre utkast" — uten autosave er
 * det tapt ved side-refresh.
 *
 * Lagringsstrategi:
 * - Ren frontend-løsning (localStorage). Ingen backend-endring.
 * - Nøkkel: `studyplan-draft-<planId|'new-<userId>'>` — én cache per plan.
 * - Periodisk lagring: hver 30 sek + debounced ved endring (2 sek).
 * - Ved vellykket "Lagre utkast" eller "Send inn" → cache slettes.
 * - Ved side-load: sammenlign cache-timestamp mot last-known DB-timestamp;
 *   hvis cache er nyere, tilby gjenoppretting.
 *
 * Størrelse: ~100-500 KB per plan. localStorage har 5-10 MB limit — vi
 * pruner andre draft-nøkler eldre enn 30 dager for å unngå å fylle opp.
 */

const STORAGE_VERSION = 1;
const DEBOUNCE_MS = 2000;
const PERIODIC_MS = 30_000;
const PRUNE_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 dager
const KEY_PREFIX = 'studyplan-draft-';

export interface DraftPayload<T> {
  version: number;
  planId: string;
  timestamp: number;
  data: T;
  meta?: {
    dbUpdatedAt?: string | null; // ISO-timestamp for siste lagrede versjon i DB
  };
}

interface UseAutosaveDraftOptions<T> {
  /** Unik nøkkel per studieplan — bruk plan-ID eller 'new' */
  planId: string;
  /** Bruker-ID — isolerer cache per bruker (task #301) */
  userId?: string | null;
  /** Objekt som skal autolagres */
  data: T;
  /** Sett false for å midlertidig pause autosave (f.eks. under KI-generering) */
  enabled?: boolean;
  /** DB-timestamp fra siste lagrede versjon (brukes ved sammenligning) */
  dbUpdatedAt?: string | null;
  /**
   * Valgfri callback som avgjør om data er "dirty" nok til å lagres.
   * Hvis den returnerer false, hopper vi over skriving — unngår at tom
   * init-state persistes og gir bruker en tom "Gjenopprett?"-modal
   * neste besøk (task #302, 2026-07-02).
   */
  isDirty?: (data: T) => boolean;
}

/**
 * Bygger localStorage-nøkkel for en studieplan.
 *
 * 2026-07-02 (task #301): Inkluderer userId i nøkkelen så cache ikke lekker
 * mellom brukere på delt nettleser. Selv om det er lite realistisk med
 * fler-bruker-nettlesere i prod, er isolasjon prinsipielt riktig.
 * Fallback til 'anon' hvis userId ikke oppgis (bakoverkompatibilitet).
 */
export function buildDraftKey(planId: string, userId?: string | null): string {
  const uid = userId || 'anon';
  return `${KEY_PREFIX}${uid}-${planId}`;
}

/**
 * Vurder om et draft har meningsfullt innhold — dvs. brukeren har faktisk
 * skrevet noe utover initial tom state. Uten denne sjekken vil autosave
 * skrive tom state til cache, og gjenopprett-modalen dukke opp neste
 * besøk med "gjenopprett tomme felter"-scenario (task #302, 2026-07-02).
 *
 * Sjekk mot studieplan-spesifikke felter. Bruk kun for StudyPlanForm-cache.
 */
export function draftHasMeaningfulContent(draft: DraftPayload<any> | null): boolean {
  if (!draft || !draft.data) return false;
  const d = draft.data.formData;
  if (!d) return false;
  const hasText = (s: unknown): boolean => typeof s === 'string' && s.trim().length > 0;
  const hasArr = (a: unknown): boolean => Array.isArray(a) && a.length > 0;

  if (hasText(d.title)) return true;
  if (hasText(d.description)) return true;
  if (hasText(d.target_group)) return true;
  if (hasText(d.admission_requirements)) return true;
  if (hasText(d.teaching_learning_methods)) return true;
  if (hasText(d.assessment_criteria)) return true;
  if (hasArr(d.content_modules)) return true;
  if (hasArr(d.categories)) return true;
  if (hasArr(d.learning_outcomes?.knowledge)) return true;
  if (hasArr(d.learning_outcomes?.skills)) return true;
  if (hasArr(d.learning_outcomes?.competence)) return true;
  // KI-generering-output regnes også som meningsfullt
  if (draft.data.preAnalysisResult) return true;
  if (draft.data.compressionResult) return true;
  if (hasArr(draft.data.outcomeEscoMappings)) return true;
  if (hasArr(draft.data.selectedEscoSkills)) return true;
  if (hasArr(draft.data.targetGroupOccupations)) return true;
  if (hasArr(draft.data.admissionOccupations)) return true;
  return false;
}

/**
 * Sletter alle drafts for en spesifikk bruker (kalles ved logout).
 */
export function clearAllDraftsForUser(userId: string): void {
  const prefix = `${KEY_PREFIX}${userId}-`;
  const toRemove: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      localStorage.removeItem(key);
    }
    if (toRemove.length > 0) {
      console.log(`[Autosave] Ryddet ${toRemove.length} drafts for bruker ved logout`);
    }
  } catch (err) {
    console.warn('[Autosave] Kunne ikke rense drafts ved logout:', err);
  }
}

/**
 * Skriver draft til localStorage. Aldri throw — hvis quota er full,
 * logger vi og prøver å prune, deretter re-lagre.
 */
export function saveDraft<T>(planId: string, data: T, dbUpdatedAt?: string | null, userId?: string | null): void {
  const key = buildDraftKey(planId, userId);
  const payload: DraftPayload<T> = {
    version: STORAGE_VERSION,
    planId,
    timestamp: Date.now(),
    data,
    meta: { dbUpdatedAt: dbUpdatedAt ?? null },
  };
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch (err) {
    // Quota exceeded — prune gamle drafts og prøv igjen
    console.warn('[Autosave] localStorage full, pruning...', err);
    pruneOldDrafts();
    try {
      localStorage.setItem(key, JSON.stringify(payload));
    } catch (err2) {
      console.error('[Autosave] Kunne ikke lagre draft selv etter prune:', err2);
    }
  }
}

/**
 * Leser draft fra localStorage. Returnerer null hvis ingen finnes,
 * eller hvis dataen er korrupt/ugyldig format.
 */
export function loadDraft<T>(planId: string, userId?: string | null): DraftPayload<T> | null {
  const key = buildDraftKey(planId, userId);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DraftPayload<T>;
    if (parsed.version !== STORAGE_VERSION) {
      console.warn('[Autosave] Draft-versjon mismatch — kastes:', parsed.version);
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch (err) {
    console.warn('[Autosave] Kunne ikke lese draft:', err);
    return null;
  }
}

/**
 * Sletter draft — kalles etter vellykket lagring.
 */
export function clearDraft(planId: string, userId?: string | null): void {
  const key = buildDraftKey(planId, userId);
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.warn('[Autosave] Kunne ikke slette draft:', err);
  }
}

/**
 * Prune drafts eldre enn PRUNE_AGE_MS. Kjøres ved quota-exceeded-feil
 * og som del av init.
 */
export function pruneOldDrafts(): void {
  const now = Date.now();
  const keysToRemove: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(KEY_PREFIX)) continue;
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        if (typeof parsed?.timestamp !== 'number') continue;
        if (now - parsed.timestamp > PRUNE_AGE_MS) {
          keysToRemove.push(key);
        }
      } catch {
        // Korrupt — merk for sletting
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
    if (keysToRemove.length > 0) {
      console.log(`[Autosave] Pruned ${keysToRemove.length} gamle drafts`);
    }
  } catch (err) {
    console.warn('[Autosave] Prune feilet:', err);
  }
}

/**
 * Avgjør om lokal draft er "nyere" enn DB-versjonen — og dermed bør
 * tilbys som gjenoppretting.
 *
 * Regler:
 * - Hvis draft.meta.dbUpdatedAt er null (planen var ny) og cache finnes →
 *   nyere per definisjon.
 * - Hvis draft.meta.dbUpdatedAt matches nåværende dbUpdatedAt → cache er
 *   fra samme "utgangspunkt" som DB. Da er den relevant hvis
 *   draft.timestamp > new Date(dbUpdatedAt).getTime().
 * - Hvis draft.meta.dbUpdatedAt IKKE matches (dvs. en annen bruker har
 *   lagret på DB siden — eller en annen tab lagret) → betrakt som
 *   utdatert (draft baserer seg på en gammel versjon).
 */
export function isDraftNewerThanDb<T>(
  draft: DraftPayload<T>,
  currentDbUpdatedAt: string | null
): boolean {
  if (!draft) return false;
  const draftBaseTimestamp = draft.meta?.dbUpdatedAt ?? null;

  if (draftBaseTimestamp === null && currentDbUpdatedAt === null) {
    // Ny plan — cache er alltid nyere enn ingenting
    return true;
  }

  if (draftBaseTimestamp !== currentDbUpdatedAt) {
    // Draft baserer seg på annen DB-versjon — betraktes som konflikt/utdatert.
    // Vis ikke gjenopprettingsmodal for å ikke overskrive nyere lagringer.
    return false;
  }

  // Samme utgangspunkt — sjekk om det er lokale endringer etter DB-lagringen
  if (!currentDbUpdatedAt) return true;
  const dbTime = new Date(currentDbUpdatedAt).getTime();
  return draft.timestamp > dbTime;
}

/**
 * React-hook: autolagrer form-state periodisk + ved endring.
 * Returnerer stabile helpers for save/load/clear som komponenten kan bruke.
 */
export function useAutosaveDraft<T>({
  planId,
  userId = null,
  data,
  enabled = true,
  dbUpdatedAt = null,
  isDirty,
}: UseAutosaveDraftOptions<T>) {
  const dataRef = useRef(data);
  const dbUpdatedAtRef = useRef(dbUpdatedAt);
  const userIdRef = useRef(userId);
  const isDirtyRef = useRef(isDirty);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const periodicTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const enabledRef = useRef(enabled);
  // Task #305 (2026-07-02): når clear() kalles etter en forkast-handling,
  // må vi hindre at unmount-cleanup skriver dataen tilbake til localStorage.
  // Denne flaggen settes av clear() og sjekkes av flushSave.
  const discardedRef = useRef(false);

  // Hold ref-ene i sync
  useEffect(() => {
    dataRef.current = data;
    dbUpdatedAtRef.current = dbUpdatedAt;
    userIdRef.current = userId;
    isDirtyRef.current = isDirty;
    enabledRef.current = enabled;
  }, [data, dbUpdatedAt, userId, isDirty, enabled]);

  // Init: prune gamle drafts én gang
  useEffect(() => {
    pruneOldDrafts();
  }, []);

  const flushSave = useCallback(() => {
    if (!enabledRef.current) return;
    // Task #305: hvis brukeren har forkastet, hopp over all fremtidig
    // skriving inkludert unmount-cleanup. Ellers vil dataen re-skrives
    // umiddelbart etter en clear() når komponenten unmountes.
    if (discardedRef.current) return;
    // Hopp over lagring hvis brukeren har oppgitt en isDirty-callback som
    // returnerer false. Hindrer at tomme init-state persistes.
    const dirtyCheck = isDirtyRef.current;
    if (dirtyCheck && !dirtyCheck(dataRef.current)) {
      return;
    }
    saveDraft(planId, dataRef.current, dbUpdatedAtRef.current, userIdRef.current);
  }, [planId]);

  // Debounce-lagring ved endring
  useEffect(() => {
    if (!enabled) return;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      flushSave();
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [data, enabled, flushSave]);

  // Periodisk lagring (heartbeat)
  useEffect(() => {
    if (!enabled) return;
    periodicTimerRef.current = setInterval(() => {
      flushSave();
    }, PERIODIC_MS);

    return () => {
      if (periodicTimerRef.current) {
        clearInterval(periodicTimerRef.current);
      }
    };
  }, [enabled, flushSave]);

  // Ved unmount / beforeunload: skriv en siste versjon
  useEffect(() => {
    const handler = () => flushSave();
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
      flushSave(); // Ved React unmount
    };
  }, [flushSave]);

  return {
    flushSave,
    // clear(): rens cache MEN la autosave fortsette. Brukes etter vellykket
    // "Lagre utkast" der brukeren blir stående i skjemaet.
    clear: useCallback(() => {
      clearDraft(planId, userIdRef.current);
    }, [planId]),
    // Task #305: discard() = rens cache + stopp all fremtidig autolagring
    // i denne komponent-instansen. Hindrer at unmount-cleanup skriver
    // dataen tilbake etter at brukeren har forkastet utkastet og skal
    // navigere bort.
    discard: useCallback(() => {
      discardedRef.current = true;
      clearDraft(planId, userIdRef.current);
    }, [planId]),
  };
}
